import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import { StopDetailedView } from '../types';
import { assignStudentsToStop } from '../../../apis/transport/stops';
import { SnackbarSeverityType } from '../../../enums';
import report, { StudentStopsReportRequestBody } from '../../../apis/report';
import { partition } from 'lodash';
import { useSchoolClassesConfigHook } from '../../common/hooks/useSchoolClassesConfig';
import { User } from '../../../types/userType';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { StudentStopsReport } from '../../StudentTransportReport/types';
import pallet from '../../../layouts/pallet';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

interface AssignStudentDialogProps {
  open: boolean;
  onClose: () => void;
  currentStop: StopDetailedView;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onStudentsAssigned: () => void;
  loggedInUser: User;
}

const AssignStudentDialog: React.FC<AssignStudentDialogProps> = ({
  open,
  onClose,
  currentStop,
  showToast,
  onStudentsAssigned,
  loggedInUser
}) => {
  const [studentName, setStudentName] = useState('');
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const { gradeOptions, divisionOptions } = useSchoolClassesConfigHook();
  const [students, setStudents] = useState<StudentStopsReport[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const getFilter = ({
    studentName,
    grades,
    divisions
  }: StudentStopsReportRequestBody) => {
    const filter: Record<string, any> = { includeStudentsWithoutStops: true };
    if (studentName && studentName.trim() !== '') {
      filter.studentName = studentName;
    }

    if (Array.isArray(grades) && grades.length > 0) {
      filter.grades = grades.map(String);
    }

    if (Array.isArray(divisions) && divisions.length > 0) {
      filter.divisions = divisions.map(String);
    }

    return filter;
  };

  useEffect(() => {
    const fetchStudents = async () => {
      if (loggedInUser?.school) {
        setIsSearching(true);
        try {
          const filter = getFilter({
            studentName,
            grades: selectedGrades,
            divisions: selectedDivisions
          });
          const response = await report.fetchStudentTransportReport(
            loggedInUser.school,
            filter
          );
          setStudents(response as unknown as StudentStopsReport[]);
        } catch (error) {
          showToast('Error fetching students', SnackbarSeverityType.error);
        } finally {
          setIsSearching(false);
        }
      }
    };
    if (open) {
      fetchStudents();
    }
    // eslint-disable-next-line
  }, [
    open,
    loggedInUser?.school,
    studentName,
    selectedGrades,
    selectedDivisions,
    showToast
  ]);

  useEffect(() => {
    if (open && currentStop?.students) {
      setSelectedStudents(currentStop.students);
    }
  }, [open, currentStop]);

  const handleToggleStudent = (studentId: string) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleClose = () => {
    setStudentName('');
    setSelectedGrades([]);
    setSelectedDivisions([]);
    setStudents([]);
    setSelectedStudents([]);
    setIsSearching(false);
    onClose();
  };

  const handleSave = async () => {
    try {
      await assignStudentsToStop(currentStop.id, selectedStudents);
      showToast(
        'Students assigned successfully!',
        SnackbarSeverityType.success
      );
      onStudentsAssigned();
      handleClose();
    } catch (error) {
      showToast('Failed to assign students.', SnackbarSeverityType.error);
    }
  };

  const sortedStudents = useMemo(() => {
    const [selected, unselected] = partition(students, (student) =>
      (currentStop?.students || []).includes(student.id)
    );
    return [...selected, ...unselected];
  }, [students, currentStop?.students]);

  return (
    <DialogModal
      open={open}
      onClose={handleClose}
      title='Assign Student to this Stop'
      actions={
        <>
          <Button onClick={handleClose} variant='outlined'>
            Cancel
          </Button>
          <Button onClick={handleSave} variant='contained'>
            Save
          </Button>
        </>
      }
    >
      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        This will let you assign the selected students to a stop in this route
        and reflect in the listing.
      </Typography>
      <TextInputWithIcon
        label='Search Student Name'
        variant='outlined'
        fullWidth
        value={studentName}
        onChange={(e) => setStudentName(e.target.value)}
        sx={{ mb: 2 }}
        placeholder='Search by name'
      />
      <Box display='flex' gap={2} mb={2}>
        <SelectAll
          label='Grade'
          placeholder='Select Grade'
          options={gradeOptions}
          selected={selectedGrades}
          onChange={setSelectedGrades}
          hasTopLabel
          sx={{ width: '50%' }}
        />
        <SelectAll
          label='Division'
          placeholder='Select Division'
          options={divisionOptions}
          selected={selectedDivisions}
          onChange={setSelectedDivisions}
          hasTopLabel
          sx={{ width: '50%' }}
        />
      </Box>
      <Box
        sx={{
          border: `1px solid ${pallet.borderGrey}`,
          borderRadius: '4px',
          position: 'relative'
        }}
      >
        <OverlayLoader show={isSearching} />
        <List sx={{ height: '400px', overflowY: 'auto' }}>
          {sortedStudents.map((student) => (
            <ListItem
              key={student.id}
              onClick={() => handleToggleStudent(student.id)}
              secondaryAction={
                <Checkbox
                  edge='end'
                  checked={selectedStudents.includes(student.id)}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    color: pallet.black,
                    '&.Mui-checked': {
                      color: pallet.black
                    }
                  }}
                />
              }
            >
              <ListItemText
                primary={`${student.name} - ${student?.courseInfo?.grade} ${student?.courseInfo?.division}`}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </DialogModal>
  );
};

export default AssignStudentDialog;
